import React, { useState, useRef, useCallback } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable voice search button.
 * Records audio from the microphone, sends it to the backend for Whisper transcription,
 * and calls onResult with the transcribed text.
 *
 * @param {function} onResult - Called with the transcribed text string
 * @param {string} className - Additional CSS classes
 * @param {string} variant - 'default' | 'header' for styling
 */
const VoiceSearchButton = ({ onResult, className = '', variant = 'default' }) => {
  const [state, setState] = useState('idle'); // idle | recording | processing
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const sendAudio = useCallback(async (blob) => {
    setState('processing');
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      const res = await axios.post(`${API}/api/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      if (res.data?.text && res.data.text.trim()) {
        onResult(res.data.text.trim());
      }
    } catch (err) {
      console.error('Voice transcription error:', err?.response?.data || err.message);
    } finally {
      setState('idle');
    }
  }, [onResult]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      streamRef.current = stream;
      chunksRef.current = [];

      // Prefer webm, fallback to other formats
      let mimeType = 'audio/webm';
      for (const type of ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size > 100) {
          sendAudio(blob);
        } else {
          setState('idle');
        }
      };

      // Request data every 250ms for more reliable chunks
      recorder.start(250);
      setState('recording');

      // Auto-stop after 10 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, 10000);
    } catch (err) {
      console.error('Microphone access error:', err);
      setState('idle');
    }
  }, [sendAudio, stopRecording]);

  const handleClick = () => {
    if (state === 'recording') {
      stopRecording();
    } else if (state === 'idle') {
      startRecording();
    }
  };

  const isHeader = variant === 'header';

  const baseClasses = `
    inline-flex items-center justify-center rounded-full transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-1
    ${state === 'processing' ? 'cursor-wait' : 'cursor-pointer'}
  `;

  const variantClasses = isHeader
    ? `w-8 h-8 ${
        state === 'recording'
          ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/40'
          : state === 'processing'
          ? 'bg-white/20 text-white/60'
          : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
      } focus:ring-white/40`
    : `w-9 h-9 ${
        state === 'recording'
          ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/40'
          : state === 'processing'
          ? 'bg-gray-100 text-gray-400'
          : 'bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600'
      } focus:ring-blue-400`;

  const iconSize = isHeader ? 'w-3.5 h-3.5' : 'w-4 h-4';

  const title = state === 'recording'
    ? 'Haz clic para detener'
    : state === 'processing'
    ? 'Procesando audio...'
    : 'Buscar por voz';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={state === 'processing'}
      className={`${baseClasses} ${variantClasses} ${className}`}
      title={title}
      data-testid="voice-search-btn"
    >
      {state === 'processing' ? (
        <Loader2 className={`${iconSize} animate-spin`} />
      ) : state === 'recording' ? (
        <MicOff className={iconSize} />
      ) : (
        <Mic className={iconSize} />
      )}
    </button>
  );
};

export default VoiceSearchButton;
